import React, { useState, useRef } from 'react';
import { InputTextarea } from 'primereact/inputtextarea';
import { Button } from 'primereact/button';
import supportTicketService from '../../services/supportTicketService';

/**
 * TicketComposer — tenant admin reply box.
 *
 * Design (2026-06-10 redesign):
 *   ONE textarea + ONE Send button. A small segmented toggle above the
 *   textarea picks the mode:
 *
 *     ⌗ Public reply   ← BLUE.  goes to the reporter, triggers email
 *     ⌗ Internal note  ← AMBER. admins only, no email
 *
 *   The textarea + Send button color-shift with the mode so a quick glance
 *   tells you what you're about to send. The single-action model means no
 *   competing Send vs Add-note buttons sitting next to each other.
 *
 * Read-only mode:
 *   When `canAct` is false (credits ticket owned by super admin), only
 *   "Internal note" mode is available — the public-reply toggle is shown
 *   disabled with a small explainer hint above it. Public-reply attempts
 *   would be rejected by the server anyway, but the UI prevents the click.
 *
 * Backend: same as before — supportTicketService.addReply(ref, {message, is_internal}).
 */
const TicketComposer = ({ ticketRef, canAct, onReplied, toastRef }) => {
  const [mode, setMode] = useState(canAct ? 'reply' : 'note');
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const textareaRef = useRef(null);

  const isNote = mode === 'note';
  const replyAllowed = canAct;

  const send = async () => {
    const msg = text.trim();
    if (!msg || sending) return;
    setSending(true);
    try {
      await supportTicketService.addReply(ticketRef, {
        message: msg,
        is_internal: isNote,
      });
      setText('');
      toastRef?.current?.show({
        severity: 'success',
        summary: isNote ? 'Internal note added' : 'Reply sent',
        detail: isNote
          ? 'Visible to your admin team only.'
          : 'Reporter will be notified by email.',
        life: 2500,
      });
      onReplied?.();
    } catch (e) {
      toastRef?.current?.show({
        severity: 'error',
        summary: 'Could not send',
        detail: e?.response?.data?.message || 'Try again.',
        life: 4000,
      });
    } finally {
      setSending(false);
    }
  };

  // Color tokens for the active mode
  const accent = isNote ? '#b45309' : 'var(--primary-color)';
  const surface = isNote ? '#fffbeb' : 'var(--surface-50)';
  const borderColor = isNote ? '#fde68a' : 'var(--surface-border)';

  return (
    <div
      className="border-round-md overflow-hidden mt-2"
      style={{ background: '#fff', border: `1px solid ${borderColor}` }}
    >
      {/* Mode toggle — segmented control */}
      <ModeToggle
        mode={mode}
        onChange={setMode}
        replyAllowed={replyAllowed}
        disabled={sending}
      />

      {/* Read-only hint when public reply is disabled */}
      {!replyAllowed && (
        <div
          className="text-xs px-3 py-2 flex align-items-center gap-2"
          style={{ background: '#fef3c7', color: '#92400e' }}
        >
          <i className="pi pi-info-circle" aria-hidden="true"></i>
          <span>This is a Credits ticket — only super admin can reply to the reporter. You can leave internal notes for your team.</span>
        </div>
      )}

      {/* Composer body */}
      <div className="p-3" style={{ background: surface }}>
        <InputTextarea
          ref={textareaRef}
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={isNote
            ? 'Internal note — only your admin team will see this'
            : 'Reply to reporter…'}
          rows={3}
          autoResize
          disabled={sending}
          className="w-full mb-2"
          style={{
            background: '#fff',
            borderColor,
            fontSize: '.9rem',
            minHeight: 60,
            maxHeight: 200,
          }}
          onKeyDown={(e) => {
            if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
              e.preventDefault();
              send();
            }
          }}
        />

        <div className="flex align-items-center justify-content-between gap-2 flex-wrap">
          <div className="text-xs" style={{ color: accent, fontWeight: 600 }}>
            <i
              className={`pi ${isNote ? 'pi-lock' : 'pi-send'} mr-1`}
              aria-hidden="true"
            ></i>
            {isNote
              ? 'Internal — only your admin team will see this'
              : 'Reporter will get an email + in-app notification'}
          </div>

          <Button
            label={sending ? 'Sending…' : 'Send'}
            icon={sending ? 'pi pi-spinner pi-spin' : 'pi pi-send'}
            onClick={send}
            disabled={sending || !text.trim() || (!replyAllowed && mode === 'reply')}
            style={{
              background: accent,
              borderColor: accent,
              fontWeight: 700,
              minWidth: 110,
            }}
          />
        </div>

        <div
          className="text-xs mt-2"
          style={{ color: 'var(--text-color-secondary)' }}
        >
          Press{' '}
          <kbd
            style={{
              background: 'var(--surface-100)',
              border: '1px solid var(--surface-border)',
              borderRadius: 3,
              padding: '.05rem .35rem',
              fontFamily: 'ui-monospace, Menlo, monospace',
              fontSize: '.65rem',
            }}
          >
            Ctrl + Enter
          </kbd>{' '}
          to send.
        </div>
      </div>
    </div>
  );
};

/**
 * Segmented toggle for switching between Public reply and Internal note.
 * Visual style mirrors radio buttons so it's clear only ONE mode is active.
 */
const ModeToggle = ({ mode, onChange, replyAllowed, disabled }) => {
  const baseStyle = {
    flex: 1,
    padding: '.6rem',
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    fontSize: '.82rem',
    fontWeight: 700,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '.4rem',
    transition: 'background .12s ease, color .12s ease',
  };

  const replyActive = mode === 'reply';
  const noteActive = mode === 'note';

  return (
    <div
      className="flex border-bottom-1 surface-border"
      style={{ background: '#fff' }}
      role="tablist"
      aria-label="Reply mode"
    >
      <button
        type="button"
        role="tab"
        aria-selected={replyActive}
        disabled={!replyAllowed || disabled}
        onClick={() => onChange('reply')}
        style={{
          ...baseStyle,
          color: replyActive ? 'var(--primary-color)' : 'var(--text-color-secondary)',
          background: replyActive ? 'var(--primary-50)' : 'transparent',
          boxShadow: replyActive ? 'inset 0 -2px 0 var(--primary-color)' : 'none',
          opacity: !replyAllowed ? 0.45 : 1,
          cursor: !replyAllowed ? 'not-allowed' : 'pointer',
        }}
      >
        <i className="pi pi-send" aria-hidden="true" style={{ fontSize: '.82rem' }}></i>
        Public reply
      </button>
      <button
        type="button"
        role="tab"
        aria-selected={noteActive}
        disabled={disabled}
        onClick={() => onChange('note')}
        style={{
          ...baseStyle,
          color: noteActive ? '#b45309' : 'var(--text-color-secondary)',
          background: noteActive ? '#fffbeb' : 'transparent',
          boxShadow: noteActive ? 'inset 0 -2px 0 #b45309' : 'none',
        }}
      >
        <i className="pi pi-lock" aria-hidden="true" style={{ fontSize: '.82rem' }}></i>
        Internal note
      </button>
    </div>
  );
};

export default TicketComposer;
